import { CategoryType, EventDateType, EventType } from "globals/types/models.ts";
import dayjs from "dayjs";
import { EventDateFormValuesType } from "utils/form.ts";
import { useAppSelector } from "config/store";
import { useMemo } from "react";

export const getClosestNextDate = <T extends EventDateFormValuesType | EventDateType>(dates: T[]): T | null => {
  const now = dayjs();
  const futureDates = dates.filter(date => {
    const dateToUse = date.endDate ? date.endDate : date.startDate;
    return now.isBefore(dateToUse, "seconds");
  });

  if (futureDates.length === 0) return null;

  //the lowest number = the closest the event
  const orderedDates = futureDates.toSorted((a, b) => {
    return dayjs(a.startDate).isAfter(dayjs(b.startDate)) ? 1 : -1;
  });

  return orderedDates[0];
};


export const useSingleEventData = (
  event: EventType | null,
  selectedDay?: string | null
) => {
  const categories: CategoryType[] = useAppSelector(state => state.global.categories);

  const googleMapsDirections = useMemo(() => {
    if (!event) return null;
    if (!event.latitude || !event.longitude) return null;
    return `https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`;
  }, [event]);

  const upcomingDate: EventDateType | null = useMemo(() => {
    if (!event) return null;
    if (event.dates.length === 0) return null;

    //try to find the date that match the selected day
    if (selectedDay) {
      for (const date of event.dates) {
        if (dayjs(date.startDate).isSame(selectedDay, "day")) {
          return date;
        }
        if (date.endDate && dayjs(date.endDate).isSame(selectedDay, "day")) {
          return date;
        }
      }
    }

    //return the closest date
    return getClosestNextDate<EventDateType>(event.dates);
  }, [selectedDay, event]);

  const pastDate: EventDateType | null = useMemo(() => {
    if (upcomingDate) return null;
    if (!event) return null;
    if (event.dates.length === 0) return null;

    return event.dates[0];
  }, [event, upcomingDate]);

  const category = useMemo(() => {
    if (!event) return null;
    return categories.find(c => c.id === event.categoryId) || null;
  }, [categories, event]);


  const fullAddress = useMemo(() => {
    if (!event) return null;
    if (!event.address) return null;

    //already full
    if (event.address.includes(",")) {
      return event.address;
    }

    const arr = [event.address, event.city, event.zipcode];

    if (event.state !== "QC") {
      arr.push(event.state);
    }
    if (event.country !== "CA") {
      arr.push(event.country);
    }

    return arr.filter(Boolean).join(", ");
  }, [event]);

  const shortAddress = useMemo(() => {
    if (!event) return null;
    if (!event.address) return null;

    let baseAddress = event.address;
    //already full
    if (event.address.includes(",")) {
      baseAddress = event.address.split(",")[0];
    }

    const arr = [
      baseAddress,
      event.city
    ];

    return arr.filter(Boolean).join(", ");
  }, [event]);

  return {
    googleMapsDirections,
    fullAddress,
    shortAddress,
    category,
    upcomingDate,
    pastDate
  };
};
